import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import {
  HomeIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  PlusCircleIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
  ClipboardDocumentListIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  BanknotesIcon,
  PresentationChartLineIcon,
  GlobeAltIcon,
  EyeIcon,
  LinkIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import { useHotelPermissions, HotelUserRole } from '../../hooks/useHotelPermissions';
import { useTranslation } from '../../i18n';
import LanguageSwitcher from '../LanguageSwitcher';
import PremiumGate from '../PremiumGate';
import { useUnreadMessageCount } from '../../hooks/useUnreadMessageCount';

interface HotelNavigationProps {
  onLogout: () => void;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
  roles?: HotelUserRole[];
  external?: boolean;
}

interface PremiumNavItem extends NavItem {
  moduleCode: string;
}

export default function HotelNavigation({ onLogout }: HotelNavigationProps) {
  const location = useLocation();
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const { user, userRole, hasPermission } = useHotelPermissions();
  const { t } = useTranslation();
  const unreadMessageCount = useUnreadMessageCount();
  const [collectorEnabled, setCollectorEnabled] = useState(false);

  useEffect(() => {
    api.get('/api/v1/hotel/settings')
      .then(res => setCollectorEnabled(!!res.data?.isCollectorEnabled))
      .catch(() => {});
  }, []);
  const fptWarRoomUrl = process.env.REACT_APP_FPT_WARROOM_URL ||
    `${window.location.protocol}//${window.location.hostname.startsWith('dev') ? 'devadmin.dief360.com' : 'admin.dief360.com'}/fpt-warroom/executive`;

  // Fermer le menu admin quand on clique en dehors
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target as Node)) {
        setIsAdminOpen(false);
      }
    }
    if (isAdminOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAdminOpen]);

  // Navigation principale
  const mainNavItems: NavItem[] = [
    { name: t('nav.home'), path: '/', icon: HomeIcon },
    { name: t('nav.policeFiles'), path: '/guests', icon: DocumentTextIcon },
    { name: t('nav.newCheckin'), path: '/checkin', icon: PlusCircleIcon },
    { name: t('nav.anomalies'), path: '/anomalies', icon: ExclamationTriangleIcon },
    { name: t('nav.messages'), path: '/messages', icon: ChatBubbleLeftRightIcon, permission: 'MESSAGES_VIEW' },
    { name: t('nav.alerts'), path: '/alerts', icon: ShieldCheckIcon, permission: 'ALERTS_VIEW' },
    ...(collectorEnabled ? [{ name: 'DIEF Collector', path: '/collector', icon: ServerStackIcon, permission: 'ADMIN_DASHBOARD' as const }] : []),
    { name: t('nav.reports'), path: '/reports', icon: ChartBarIcon, permission: 'REPORTS_VIEW' },
  ];

  // Navigation admin
  const adminNavItems: NavItem[] = [
    {
      name: t('nav.adminDashboard'),
      path: '/admin',
      icon: ChartBarIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: t('nav.staffManagement'),
      path: '/admin/staff',
      icon: UsersIcon,
      permission: 'STAFF_VIEW'
    },
    {
      name: t('nav.hotelSettings'),
      path: '/admin/settings',
      icon: Cog6ToothIcon,
      permission: 'SETTINGS_VIEW'
    },
    {
      name: t('nav.roomTypes'),
      path: '/admin/room-types',
      icon: BuildingOfficeIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: t('nav.rooms'),
      path: '/admin/rooms',
      icon: HomeIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: t('nav.rates'),
      path: '/admin/rates',
      icon: CurrencyDollarIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: t('nav.kyc'),
      path: '/admin/kyc',
      icon: DocumentCheckIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: t('nav.hotelEnriched'),
      path: '/admin/hotel-enriched',
      icon: ClipboardDocumentListIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: t('nav.restaurants'),
      path: '/admin/restaurants',
      icon: BuildingOfficeIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: t('nav.connectors'),
      path: '/connectors',
      icon: LinkIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: 'FPT War Room',
      path: fptWarRoomUrl,
      icon: CurrencyDollarIcon,
      permission: 'ADMIN_DASHBOARD',
      roles: ['hotel_admin'],
      external: true,
    },
    {
      name: 'Declarations V4',
      path: '/fpt/v4/declarations',
      icon: DocumentTextIcon,
      permission: 'ADMIN_DASHBOARD'
    },
    {
      name: 'Paiements V4',
      path: '/fpt/v4/payments',
      icon: BanknotesIcon,
      permission: 'ADMIN_DASHBOARD'
    },
  ];

  // Navigation premium — chaque item est protégé par PremiumGate
  const premiumNavItems: PremiumNavItem[] = [
    {
      name: 'Room Service',
      path: '/room-service',
      icon: SparklesIcon,
      moduleCode: 'ROOM_SERVICE',
    },
    {
      name: 'POS / Restaurant',
      path: '/pos',
      icon: ComputerDesktopIcon,
      moduleCode: 'POS_RESTAURANT',
    },
    {
      name: 'Billing Avancé',
      path: '/billing',
      icon: BanknotesIcon,
      moduleCode: 'BILLING_AVANCE',
    },
    {
      name: 'Analytics',
      path: '/analytics',
      icon: PresentationChartLineIcon,
      moduleCode: 'ANALYTICS',
    },
    {
      name: 'Distribution',
      path: '/distribution',
      icon: GlobeAltIcon,
      moduleCode: 'DISTRIBUTION',
    },
    {
      name: 'Supervision DIEF',
      path: '/supervision',
      icon: EyeIcon,
      moduleCode: 'SUPERVISION_DIEF',
    },
  ];

  // Filtrer les items selon les permissions
  const visibleMainItems = mainNavItems.filter(item => 
    !item.permission || hasPermission(item.permission as any)
  );

  const visibleAdminItems = adminNavItems.filter(item => 
    (!item.permission || hasPermission(item.permission as any)) &&
    (!item.roles || item.roles.includes(userRole))
  );

  const showAdminSection = visibleAdminItems.length > 0;

  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navLinkClass = (path: string): string => {
    const baseClass = "flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors";
    return isActive(path)
      ? `${baseClass} bg-purple-700 text-white`
      : `${baseClass} text-purple-100 hover:bg-purple-700 hover:text-white`;
  };

  return (
    <nav className="bg-purple-900 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo et navigation principale */}
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-white mr-2" />
              <span className="text-xl font-bold text-white">Hotel Portal</span>
            </Link>

            {/* Navigation principale - Desktop */}
            <div className="hidden lg:flex items-center space-x-1 ml-6">
              {visibleMainItems.slice(1).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={navLinkClass(item.path)}
                >
                  <item.icon className="h-5 w-5 mr-1.5" />
                  {item.name}
                  {item.path === '/messages' && unreadMessageCount > 0 && (
                    <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] leading-4 text-white">
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </span>
                  )}
                </Link>
              ))}

              {/* Items premium — chacun protégé par PremiumGate en mode inline */}
              {premiumNavItems.map((item) => (
                <PremiumGate key={item.path} moduleCode={item.moduleCode} inline>
                  <Link
                    to={item.path}
                    className={navLinkClass(item.path)}
                  >
                    <item.icon className="h-5 w-5 mr-1.5" />
                    {item.name}
                  </Link>
                </PremiumGate>
              ))}

              {/* Dropdown Admin si accessible */}
              {showAdminSection && (
                <div className="relative" ref={adminMenuRef}>
                  <button
                    onClick={() => setIsAdminOpen(!isAdminOpen)}
                    className={`${navLinkClass('/admin')} flex items-center`}
                  >
                    <Cog6ToothIcon className="h-5 w-5 mr-1.5" />
                    {t('nav.admin')}
                    {isAdminOpen ? (
                      <ChevronUpIcon className="h-4 w-4 ml-1" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </button>

                  {/* Dropdown menu */}
                  {isAdminOpen && (
                    <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        {visibleAdminItems.map((item) => {
                          const className = `flex items-center px-4 py-2 text-sm ${
                            isActive(item.path)
                              ? 'text-purple-700 font-semibold bg-purple-50'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`;
                          const content = <><item.icon className={`h-5 w-5 mr-2 ${isActive(item.path) ? 'text-purple-600' : 'text-gray-400'}`} />{item.name}</>;
                          return item.external ? (
                            <a key={item.path} href={item.path} target="_blank" rel="noreferrer" className={className} onClick={() => setIsAdminOpen(false)}>{content}</a>
                          ) : (
                            <Link key={item.path} to={item.path} className={className} onClick={() => setIsAdminOpen(false)}>{content}</Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* User info et logout */}
          <div className="flex items-center space-x-4">
            {user && (
              <div className="hidden md:flex items-center text-white text-sm">
                <div className="mr-3">
                  <div className="font-medium">{user.name || user.email}</div>
                  <div className="text-purple-200 text-xs">
                    {user.hotelName || 'Hotel'} • {getRoleLabel(userRole)}
                  </div>
                </div>
              </div>
            )}
            <LanguageSwitcher />
            <button
              onClick={onLogout}
              className="hidden sm:block px-4 py-2 text-sm text-white hover:text-purple-200 transition font-medium"
            >
              {t('nav.logout')}
            </button>
            {/* Hamburger toggle for mobile */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-purple-200 hover:text-white"
              aria-label="Menu de navigation"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Navigation secondaire - Mobile (collapsible) */}
        {isMobileMenuOpen && <div className="lg:hidden pb-3 space-y-1">
          {visibleMainItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={navLinkClass(item.path)}
            >
              <item.icon className="h-5 w-5 mr-2" />
              {item.name}
            </Link>
          ))}

          {/* Items premium mobile — chacun protégé par PremiumGate en mode inline */}
          {premiumNavItems.map((item) => (
            <PremiumGate key={item.path} moduleCode={item.moduleCode} inline>
              <Link
                to={item.path}
                className={navLinkClass(item.path)}
              >
                <item.icon className="h-5 w-5 mr-2" />
                {item.name}
                {item.path === '/messages' && unreadMessageCount > 0 && (
                  <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] leading-4 text-white">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </Link>
            </PremiumGate>
          ))}

          {/* Section Admin - Mobile */}
          {showAdminSection && (
            <>
              <div className="pt-2 pb-1">
                <div className="text-purple-300 text-xs font-semibold uppercase tracking-wider px-3">
                  {t('nav.admin')}
                </div>
              </div>
              {visibleAdminItems.map((item) => item.external ? (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noreferrer"
                  className={navLinkClass(item.path)}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <item.icon className="h-5 w-5 mr-2" />
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={navLinkClass(item.path)}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <item.icon className="h-5 w-5 mr-2" />
                  {item.name}
                </Link>
              ))}
            </>
          )}
          {/* Mobile logout */}
          <button
            onClick={onLogout}
            className="w-full text-left flex items-center px-3 py-2 rounded-md text-sm font-medium text-purple-100 hover:bg-purple-700 hover:text-white mt-2 border-t border-purple-700 pt-3"
          >
            Deconnexion
          </button>
        </div>}
      </div>
    </nav>
  );
}

function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    'hotel_admin': 'Administrateur',
    'hotel_manager': 'Gestionnaire',
    'receptionist': 'Réceptionniste',
    'staff': 'Personnel'
  };
  return labels[role] || role;
}
